import React, { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import api from '../utils/api';
import { inr, num, dt, apiError, downloadCSV } from '../utils/format';

const TABS = [
  { key: 'plan',         label: 'To Prepare',         dated: true },
  { key: 'inventory',    label: 'Inventory',          dated: false },
  { key: 'verification', label: 'Stock Verification', dated: false },
  { key: 'stock',        label: 'Raw Material Stock', dated: false },
  { key: 'consumption',  label: 'Consumption',        dated: true },
  { key: 'production',   label: 'Production Summary',  dated: true },
];

const fmtMins = (m) => (m == null ? '—' : m >= 60 ? `${Math.floor(m / 60)}h ${m % 60}m` : `${m}m`);

const Stat = ({ label, value, tone }) => (
  <div className="card card-pad stat">
    <div className="stat-label">{label}</div>
    <div className={`stat-value ${tone || ''}`}>{value}</div>
  </div>
);

const Reports = () => {
  const [tab, setTab] = useState('plan');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [locations, setLocations] = useState([]);
  const [verifyLoc, setVerifyLoc] = useState('');
  const [counted, setCounted] = useState({});   // stock-verification: manual physical counts

  const active = TABS.find((t) => t.key === tab);

  useEffect(() => { api.get('/locations').then(({ data }) => setLocations(data.locations || [])).catch(() => {}); }, []);

  const load = useCallback(() => {
    const url = {
      plan: '/reports/production-plan',
      inventory: '/reports/inventory',
      verification: verifyLoc ? `/reports/stock-verification?locationId=${verifyLoc}` : null,
      stock: '/reports/raw-material-stock',
      consumption: '/reports/raw-material-consumption',
      production: '/reports/production-summary',
    }[tab];
    if (!url) { setData(null); return; }
    setLoading(true); setData(null);
    const qs = active.dated && (from || to) ? `${url.includes('?') ? '&' : '?'}${from ? `from=${from}` : ''}${from && to ? '&' : ''}${to ? `to=${to}` : ''}` : '';
    api.get(url + qs).then(({ data }) => setData(data)).catch((e) => toast.error(apiError(e))).finally(() => setLoading(false));
  }, [tab, from, to, active, verifyLoc]);
  useEffect(load, [load]);
  useEffect(() => { setCounted({}); }, [tab, verifyLoc]);

  const exportCSV = () => {
    if (!data?.rows?.length) return;
    if (tab === 'inventory') {
      const locCols = (data.locations || []).map((l) => ({ key: `loc_${l.id}`, label: l.name }));
      const cols = [{ key: 'product', label: 'Product' }, { key: 'variant', label: 'Pack' }, { key: 'unit', label: 'Unit' }, ...locCols, { key: 'total', label: 'Total' }];
      const rows = data.rows.map((r) => ({ ...r, ...Object.fromEntries((data.locations || []).map((l) => [`loc_${l.id}`, r.byLoc[l.id] || 0])) }));
      return downloadCSV('inventory-report.csv', cols, rows);
    }
    if (tab === 'verification') {
      const cols = [{ key: 'product', label: 'Product' }, { key: 'variant', label: 'Pack' }, { key: 'unit', label: 'Unit' }, { key: 'systemQty', label: 'System qty' }, { key: 'counted', label: 'Counted' }, { key: 'variance', label: 'Variance' }];
      const rows = data.rows.map((r, i) => { const c = counted[i]; const cv = c === '' || c == null ? null : Number(c); return { ...r, counted: cv ?? '', variance: cv == null ? '' : (cv - r.systemQty) }; });
      return downloadCSV(`stock-verification-${data.location || ''}.csv`, cols, rows);
    }
    const cols = {
      stock: [['name', 'Material'], ['kind', 'Kind'], ['unit', 'Unit'], ['onHand', 'On hand'], ['purchasePrice', 'Cost/unit'], ['value', 'Value'], ['minStock', 'Min'], ['belowMin', 'Below min']],
      consumption: [['name', 'Material'], ['unit', 'Unit'], ['qty', 'Qty consumed'], ['cost', 'Cost']],
      production: [['code', 'Batch'], ['product', 'Product'], ['variant', 'Variant'], ['producedQty', 'Produced'], ['wastageQty', 'Wastage'], ['minutesToMake', 'Minutes to make'], ['totalCost', 'Total cost'], ['costPerUnit', 'Cost/unit'], ['status', 'Status']],
      plan: [['product', 'Product'], ['variant', 'Pack size'], ['units', 'Units requested'], ['bulkKg', 'Kg to make'], ['branches', 'Branches']],
    }[tab].map(([key, label]) => ({ key, label }));
    downloadCSV(`${tab}-report.csv`, cols, data.rows);
  };

  const t = data?.totals;

  return (
    <div>
      <div className="section-head">
        <div />
        <div className="flex gap">
          {active.dated && (
            <>
              <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} title="From" style={{ width: 150 }} />
              <input type="date" value={to} onChange={(e) => setTo(e.target.value)} title="To" style={{ width: 150 }} />
            </>
          )}
          {tab === 'verification' && (
            <select value={verifyLoc} onChange={(e) => setVerifyLoc(e.target.value)} style={{ width: 200 }}>
              <option value="">— select branch —</option>
              {locations.map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
            </select>
          )}
          <button className="btn btn-outline btn-sm" onClick={exportCSV} disabled={!data?.rows?.length}>Export CSV</button>
        </div>
      </div>

      <div className="tabs" style={{ marginBottom: '1rem' }}>
        {TABS.map((x) => (
          <button key={x.key} className={`tab${tab === x.key ? ' active' : ''}`} onClick={() => setTab(x.key)}>{x.label}</button>
        ))}
      </div>

      {loading ? <div className="spinner" /> : !data ? (
        tab === 'verification' && !verifyLoc ? <p className="muted">Select a branch above to generate its stock-verification sheet.</p> : null
      ) : (
        <>
          {/* Summary tiles */}
          {tab === 'plan' && t && (
            <div className="stat-row">
              <Stat label="Open requests" value={num(t.requests, 0)} />
              <Stat label="Products to make" value={num(t.products, 0)} />
              <Stat label="Total to make" value={`${num(t.bulkKg)} kg`} />
            </div>
          )}
          {tab === 'inventory' && t && (
            <div className="stat-row">
              <Stat label="Products / packs" value={num(t.items, 0)} />
              <Stat label="In stock" value={num(t.inStock, 0)} />
              <Stat label="Total quantity" value={num(t.totalQty)} />
              <Stat label="Locations" value={num((data.locations || []).length, 0)} />
            </div>
          )}
          {tab === 'verification' && t && (
            <div className="stat-row">
              <Stat label="Branch" value={data.location || '—'} />
              <Stat label="Products / packs" value={num(t.items, 0)} />
              <Stat label="In stock" value={num(t.inStock, 0)} />
            </div>
          )}
          {tab === 'stock' && t && (
            <div className="stat-row">
              <Stat label="Materials" value={num(t.materials, 0)} />
              <Stat label="Total stock value" value={inr(t.totalValue)} />
              <Stat label="Below minimum" value={num(t.belowMin, 0)} tone={t.belowMin ? 'warn' : ''} />
            </div>
          )}
          {tab === 'consumption' && t && (
            <div className="stat-row">
              <Stat label="Materials used" value={num(t.materials, 0)} />
              <Stat label="Total consumption cost" value={inr(t.totalCost)} />
            </div>
          )}
          {tab === 'production' && t && (
            <div className="stat-row">
              <Stat label="Batches" value={`${num(t.ready, 0)} / ${num(t.batches, 0)} ready`} />
              <Stat label="Units produced" value={num(t.unitsProduced, 0)} />
              <Stat label="Total cost" value={inr(t.totalCost)} />
              <Stat label="Avg cost / unit" value={t.avgCostPerUnit != null ? inr(t.avgCostPerUnit) : '—'} />
              <Stat label="Avg time to make" value={fmtMins(t.avgMinutesToMake)} />
              <Stat label="Wastage" value={num(t.totalWastage)} />
            </div>
          )}

          <div className="table-wrap">
            {tab === 'plan' && (
              <table className="grid">
                <thead><tr><th>Product</th><th>Pack size</th><th className="right">Units requested</th><th className="right">Kg to make</th><th>Requesting branches</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i}>
                      <td><strong>{r.product}</strong></td><td>{r.variant}</td>
                      <td className="right">{num(r.units, 0)}</td>
                      <td className="right">{num(r.bulkKg)} kg</td>
                      <td className="muted">{r.branches}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={5} className="empty">No pending stock requests — nothing to prepare.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'inventory' && (
              <table className="grid">
                <thead><tr><th>Product</th><th>Pack</th>{(data.locations || []).map((l) => <th key={l.id} className="right">{l.name}</th>)}<th className="right">Total</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => (
                    <tr key={i}>
                      <td><strong>{r.product}</strong></td><td>{r.variant}</td>
                      {(data.locations || []).map((l) => <td key={l.id} className="right">{r.byLoc[l.id] > 0 ? num(r.byLoc[l.id]) : <span className="muted">0</span>}</td>)}
                      <td className="right"><strong>{num(r.total)}</strong> {r.unit}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={(data.locations || []).length + 3} className="empty">No product stock on hand anywhere.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'verification' && (
              <table className="grid">
                <thead><tr><th>Product</th><th>Pack</th><th className="right">System qty</th><th className="right">Counted</th><th className="right">Variance</th></tr></thead>
                <tbody>
                  {data.rows.map((r, i) => {
                    const c = counted[i];
                    const cv = c === '' || c == null ? null : Number(c);
                    const v = cv == null ? null : cv - r.systemQty;
                    return (
                      <tr key={i}>
                        <td><strong>{r.product}</strong></td><td>{r.variant}</td>
                        <td className="right">{num(r.systemQty)} {r.unit}</td>
                        <td className="right"><input type="number" min="0" step="0.001" style={{ width: 90 }} value={c ?? ''} onChange={(e) => setCounted((p) => ({ ...p, [i]: e.target.value }))} /></td>
                        <td className="right">{v == null ? '—' : <span className={Math.abs(v) > 1e-9 ? 'neg' : 'muted'}>{v > 0 ? '+' : ''}{num(v)}</span>}</td>
                      </tr>
                    );
                  })}
                  {!data.rows.length && <tr><td colSpan={5} className="empty">No products.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'stock' && (
              <table className="grid">
                <thead><tr><th>Material</th><th>Kind</th><th>Unit</th><th className="right">On hand</th><th className="right">Cost/unit</th><th className="right">Value</th><th className="right">Min</th><th>Status</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.id}>
                      <td><strong>{r.name}</strong></td><td>{r.kind}</td><td>{r.unit}</td>
                      <td className="right">{num(r.onHand)}</td><td className="right">{inr(r.purchasePrice)}</td>
                      <td className="right">{inr(r.value)}</td><td className="right">{num(r.minStock)}</td>
                      <td>{r.belowMin ? <span className="badge badge-red">Reorder</span> : <span className="badge badge-green">OK</span>}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={8} className="empty">No raw materials.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'consumption' && (
              <table className="grid">
                <thead><tr><th>Material</th><th className="right">Qty consumed</th><th>Unit</th><th className="right">Cost</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.rawMaterialId}><td><strong>{r.name}</strong></td><td className="right">{num(r.qty)}</td><td>{r.unit}</td><td className="right">{inr(r.cost)}</td></tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={4} className="empty">No production consumption in this period.</td></tr>}
                </tbody>
              </table>
            )}
            {tab === 'production' && (
              <table className="grid">
                <thead><tr><th>Batch</th><th>Product</th><th>Variant</th><th className="right">Produced</th><th className="right">Wastage</th><th className="right">Time to make</th><th className="right">Total cost</th><th className="right">Cost/unit</th><th>Status</th><th>When</th></tr></thead>
                <tbody>
                  {data.rows.map((r) => (
                    <tr key={r.code}>
                      <td className="mono">{r.code}</td><td>{r.product}</td><td>{r.variant}</td>
                      <td className="right">{r.status === 'ready' ? num(r.producedQty, 0) : '—'}</td>
                      <td className="right">{num(r.wastageQty)}</td>
                      <td className="right">{fmtMins(r.minutesToMake)}</td>
                      <td className="right">{inr(r.totalCost)}</td>
                      <td className="right">{r.costPerUnit != null ? inr(r.costPerUnit) : '—'}</td>
                      <td><span className={`badge ${r.status === 'ready' ? 'badge-green' : r.status === 'cancelled' ? 'badge-red' : 'badge-gold'}`}>{r.status.replace('_', ' ')}</span></td>
                      <td className="muted">{dt(r.createdAt)}</td>
                    </tr>
                  ))}
                  {!data.rows.length && <tr><td colSpan={10} className="empty">No production in this period.</td></tr>}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default Reports;
